// ══════════════════════════════════════════════════════════════
// Session log — append-only record of every item the kid sees
// ══════════════════════════════════════════════════════════════
//
// Purpose: when a bug surfaces (Sasha says "it showed me 0+0 with weird
// choices"), we have a record of exactly what was rendered. No more
// guessing from screenshots.
//
// Storage: localStorage key `pw.sessionLog`. Capped at the most recent
// MAX_EVENTS to prevent unbounded growth. Each event is a small JSON
// blob. The log is a flat ring buffer keyed by event id, ordered by
// timestamp.
//
// Privacy: never written to network. Cleared by the user via the
// settings UI (or `pencilwise.clearLog()` in DevTools).
//
// Shape of an event:
//   {
//     ts:        "2026-05-04T01:23:45.678Z",
//     type:      "render" | "answer" | "session_start" | "session_end",
//     sessionId: "abc123",                    // groups events from one session
//     itemId:    "item_42_lk7c8s",
//     moduleId:  "reading.0a",
//     itemType:  "rhyme",                     // item.type
//     visualType:"word_choice_grid",
//     prompt:    "Which word rhymes with 'cat'?",
//     correctAnswer: "bat",
//     distractors: ["bus","pen"],
//     // for "answer" events:
//     picked:    "bus",
//     correct:   false,
//     latencyMs: 4231,
//   }

const KEY = "pw.sessionLog";
const MAX_EVENTS = 500; // ~last several sessions

function safeRead() {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Internal event shape — permissive on purpose; this is a bag-of-fields
// telemetry log, not a typed event bus.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LogEvent = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ItemLike = any;

function safeWrite(events: LogEvent[]) {
  if (typeof localStorage === "undefined") return;
  try {
    const trimmed = events.length > MAX_EVENTS ? events.slice(-MAX_EVENTS) : events;
    localStorage.setItem(KEY, JSON.stringify(trimmed));
  } catch (err) {
    // Likely quota exceeded — drop half the buffer and retry once.
    try {
      const half = events.slice(Math.floor(events.length / 2));
      localStorage.setItem(KEY, JSON.stringify(half));
    } catch { /* give up silently */ }
  }
}

function append(event: LogEvent) {
  const events = safeRead();
  events.push(event);
  safeWrite(events);
}

let _currentSessionId: string | null = null;

// Snapshot of the item, stripped of large fields. Keeps the log compact.
function summarize(item: ItemLike) {
  if (!item) return {};
  return {
    itemId: item.id,
    moduleId: item.moduleId,
    itemType: item.type,
    visualType: item.visualType,
    prompt: typeof item.prompt === "string" ? item.prompt.slice(0, 240) : null,
    correctAnswer: item.correctAnswer,
    distractors: Array.isArray(item.distractors) ? item.distractors.slice(0, 6) : [],
    responseMode: item.responseMode,
  };
}

export function logRender(item: ItemLike) {
  if (!item) return;
  append({
    ts: new Date().toISOString(),
    type: "render",
    sessionId: _currentSessionId,
    ...summarize(item),
  });
}

interface LogAnswerArgs { picked?: unknown; correct?: boolean; latencyMs?: number; ocrRecognized?: string | null }
// ── Phase-1 SILENT reading-rate evidence ──────────────────────────────
//
// wcpm Phase 1: ReadingSession used to COMPUTE the latency-based reading
// rate and then DISCARD it. We now CAPTURE it here as a silent telemetry
// event carrying the RESERVED `RateEvidence` (kind: "reading_rate") shape.
//
// This REUSES the existing `pw.sessionLog` localStorage telemetry store
// (no new store, no DB schema migration) — the same additively-extensible
// bag-of-fields log that already records per-item answer events. It is
// PURE TELEMETRY: nothing in the mastery / progression / selection path
// reads this store, so persisting the rate here CANNOT change any child's
// mastery band. A FUTURE fluency channel consumes these records; today the
// signal is recorded as a within-child trend ONLY (no fluent/not-fluent
// verdict is computed or surfaced — see DEFAULT_TARGET_WCPM caveat in
// readingRate.ts).

import type { RateEvidence } from "@/curriculum/types/accelerationShapes";

/** Append one reading-rate `RateEvidence` record to the session log.
 *  SILENT Phase-1 capture: persisted for a future fluency channel, kept
 *  OUT of the mastery band. Fail-safe: no-op on a falsy/non-object arg. */
export function logRateEvidence(evidence: RateEvidence) {
  if (!evidence || typeof evidence !== "object") return;
  append({
    ts: evidence.timestamp ?? new Date().toISOString(),
    type: "reading_rate",
    sessionId: _currentSessionId,
    // Embed the full reserved RateEvidence shape so a future fluency
    // channel can read it back verbatim (no lossy flattening).
    rateEvidence: evidence,
  });
}

function getLog() {
  return safeRead();
}

function clearLog() {
  safeWrite([]);
}

function exportLog() {
  return JSON.stringify(safeRead(), null, 2);
}

// Devtool convenience: paste `pencilwise.copyLog()` in DevTools to grab
// the JSON blob. Wires onto window in the browser only.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const w = (typeof window !== "undefined" ? window : {}) as any;
if (typeof window !== "undefined") {
  w.pencilwise = w.pencilwise || {};
  w.pencilwise.getLog = getLog;
  w.pencilwise.clearLog = clearLog;
  w.pencilwise.exportLog = exportLog;
  w.pencilwise.copyLog = () => {
    const blob = exportLog();
    if (navigator.clipboard) navigator.clipboard.writeText(blob);
    console.log(`Copied ${getLog().length} events to clipboard.`);
    return blob;
  };
}
